import type { EditorialRepository } from "@/data/repository"
import { DomainError } from "@/data/repository"
import { createImageThumbnail, immutableCacheControl, thumbnailStoragePath } from "@/lib/image-thumbnail"
import { imprintLogoFileError } from "@/lib/imprint-logos"
import { createSignedUrlCache } from "@/lib/signed-url-cache"
import { supabase } from "@/lib/supabase"
import type {
  AssignableUser,
  MyTask,
  SetProductionLeadInput,
  SetProductionStageAssignmentInput,
  SaveProductionScheduleInput,
  SetUserTeamInput,
  UpdateUserInput,
  BlockItemInput,
  CatalogFilters,
  CatalogCollectionInput,
  CatalogCollectionSummary,
  CatalogItem,
  CatalogItemActivity,
  CatalogItemDocument,
  CatalogItemMockupAsset,
  CatalogItemEditionSnapshot,
  CatalogItemInput,
  CatalogItemType,
  CatalogPage,
  CatalogPagination,
  CatalogProduct,
  CatalogProductImage,
  CatalogProductImagePreview,
  ReplaceStudioKitImagesResult,
  StagedCatalogProductImage,
  DriveSyncResult,
  GoogleDriveConnection,
  SyncProductionWithDriveInput,
  CatalogPublicMediaSelection,
  CatalogImageAltText,
  SaveCatalogPublicMediaOptions,
  CatalogProductSummary,
  CatalogSearchResult,
  CreateKitTemplateInput,
  CreatePipelineTemplateInput,
  CreateCatalogProductInput,
  CreateProductDriveFoldersInput,
  DashboardData,
  EditorialFunction,
  DeleteCatalogItemDocumentInput,
  DeleteCatalogItemDocumentResult,
  DeleteCatalogItemMockupAssetInput,
  DeleteCatalogItemMockupAssetResult,
  DeleteCatalogProductImageInput,
  DeleteCatalogProductImageResult,
  DeleteCatalogProductResult,
  DriveItemStageFolders,
  CatalogRecordExtraction,
  UploadSupplementaryFileInput,
  SupplementaryFileKind,
  CreditsExtraction,
  FaoExtraction,
  ProductDriveFoldersResult,
  DriveUploadedFile,
  ImprintInput,
  ImprintLogo,
  ImprintLogoTarget,
  ImprintSummary,
  InviteUserInput,
  ItemTypeInput,
  ItemTypeVariantInput,
  NotificationItem,
  PipelineCatalogEntry,
  PipelineStageEstimate,
  SavePipelineStageEstimatesInput,
  PipelineTemplateVersion,
  ProductionFilters,
  ProductionRun,
  ProductionRunSummary,
  PipelineVersionContentInput,
  PrintShopInput,
  PrintShopSummary,
  TemplateCatalog,
  TransitionItemInput,
  SkipProductionItemInput,
  ReturnProductionItemInput,
  SaveCatalogProductInput,
  StartProductionInput,
  UpdateProductionInput,
  UpdateProductionQuantitiesInput,
  UploadCatalogItemDocumentInput,
  UploadCatalogItemMockupAssetInput,
  UploadCatalogProductImageInput,
  UploadToDriveStageInput,
  KitPresetCatalogEntry,
  WorkspaceUser,
  UploadImprintLogoInput,
  ProductionPrintDelivery,
  ProductionPrintItem,
} from "@/types/domain"
import { SUPPLEMENTARY_FILE_TYPES } from "../../supabase/functions/_shared/catalog-record-extraction"
import type { PrintChecklistKey } from "@/lib/print-checklist"

type LegacyPipelineVersion = Omit<PipelineTemplateVersion, "stages"> & {
  stages: Array<Omit<PipelineTemplateVersion["stages"][number], "checklists"> & {
    checklists: Array<PipelineTemplateVersion["stages"][number]["checklists"][number] & { itemTypeId?: string }>
  }>
}

function normalizePipelineVersions(versions: LegacyPipelineVersion[]): PipelineTemplateVersion[] {
  return versions.map((version) => ({
    ...version,
    stages: version.stages.map((stage) => ({
      ...stage,
      checklists: stage.checklists.map(({ itemTypeId, ...checklist }) => ({
        ...checklist,
        itemTypeIds: checklist.itemTypeIds ?? (itemTypeId ? [itemTypeId] : []),
      })),
    })),
  }))
}

async function invokeFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
  const { data, error } = await supabase.functions.invoke<T>(name, { body })
  if (error) {
    const context = (error as { context?: unknown }).context
    const payload = context instanceof Response ? await context.json().catch(() => null) as { code?: string; message?: string } | null : null
    throw new DomainError(payload?.code ?? "FUNCTION_ERROR", payload?.message ?? "Não foi possível concluir a operação. Tente novamente.")
  }
  return data as T
}

// Envia os bytes do arquivo diretamente ao Google via a URL de sessão retornada pelo backend; sem cabeçalho de autorização (a própria URL é a credencial).
function putFileToGoogleDrive(uploadUrl: string, file: File, mimeType: string, onProgress?: (fraction: number) => void): Promise<{ id: string; name: string; webViewLink?: string }> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open("PUT", uploadUrl, true)
    request.setRequestHeader("Content-Type", mimeType)
    request.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onProgress) return
      onProgress(event.loaded / event.total)
    }
    request.onload = () => {
      if (request.status === 200 || request.status === 201) {
        try {
          resolve(JSON.parse(request.responseText) as { id: string; name: string; webViewLink?: string })
        } catch {
          reject(new DomainError("DRIVE_UPLOAD_FAILED", "O Google Drive retornou uma resposta inesperada."))
        }
        return
      }
      reject(new DomainError("DRIVE_UPLOAD_FAILED", "O envio para o Google Drive falhou. Tente novamente."))
    }
    request.onerror = () => reject(new DomainError("DRIVE_UPLOAD_FAILED", "Não foi possível enviar o arquivo. Verifique a conexão e tente novamente."))
    request.send(file)
  })
}

async function rpc<T>(name: string, params: Record<string, unknown> = {}): Promise<T> {
  if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
  const { data, error } = await supabase.rpc(name, params)
  if (error) {
    const domainCode = /^[A-Z][A-Z0-9_]+$/.test(error.message) ? error.message : (error.code ?? "SUPABASE_ERROR")
    let details: Record<string, unknown> = { hint: error.hint, databaseDetails: error.details }
    if (error.details?.startsWith("{") || error.details?.startsWith("[")) {
      try { details = { ...details, parsed: JSON.parse(error.details) } as Record<string, unknown> } catch { /* keep the raw database detail */ }
    }
    const messages: Record<string, string> = {
      CONCURRENCY_CONFLICT: "Este registro foi alterado por outra pessoa. Atualize e tente novamente.",
      BLOCKING_CHECKLISTS: "Conclua os controles obrigatórios antes de avançar.",
      FORBIDDEN: "Seu papel não permite esta operação.",
      INVALID_STAGE_TRANSITION: "A etapa selecionada não permite essa transição.",
      CURRENT_STAGE_REQUIRED: "O item precisa ter uma etapa em andamento.",
      REASON_REQUIRED: "Informe uma justificativa para continuar.",
      INVALID_STAGE_DAYS: "Revise as durações das etapas: use de 0 a 999 dias úteis.",
      INVALID_TARGET_END_DATE: "Informe uma data final prevista válida.",
      DRAFT_REQUIRED: "Somente uma versão em rascunho pode ser alterada.",
      DRAFT_ALREADY_EXISTS: "Este modelo já possui uma versão em rascunho.",
      TEMPLATE_VERSION_PUBLISHED: "Versões publicadas são imutáveis. Crie uma nova versão.",
      PIPELINE_VERSION_IN_USE: "Esta versão já foi usada por uma produção. Crie uma nova versão para preservar o histórico.",
      PIPELINE_IN_USE: "Este fluxo de trabalho já foi usado em uma produção e não pode ser apagado.",
      ACTIVE_PIPELINE_REQUIRED: "Reative o fluxo de trabalho antes de usá-lo em uma nova produção.",
      PUBLISHED_TEMPLATES_REQUIRED: "Publique um modelo de kit e um fluxo de trabalho para este selo antes de criar produtos.",
      DUPLICATE_IMPRINT: "Já existe um selo com esse código ou nome.",
      DUPLICATE_CATALOG_COLLECTION: "Já existe uma coleção com esse nome neste selo.",
      INVALID_CATALOG_COLLECTION: "Selecione uma coleção válida do mesmo selo do produto.",
      CATALOG_COLLECTION_HAS_PRODUCTS: "Esta coleção possui produtos associados.",
      IMPRINT_HAS_PRODUCTS: "Este selo possui produtos associados e não pode ser apagado.",
      IMPRINT_HAS_KIT_TEMPLATES: "Este selo possui modelos de kit associados e não pode ser apagado.",
      IMPRINT_HAS_COLLECTIONS: "Este selo possui coleções associadas e não pode ser apagado.",
      IMPRINT_INACTIVE: "Ative o selo antes de criar novos produtos ou modelos.",
      DUPLICATE_ISBN: "Já existe uma edição cadastrada com este ISBN.",
      INVALID_ISBN: "Informe um ISBN-10 ou ISBN-13 válido.",
      INCOMPLETE_FAO: "Preencha autor, sinopse, contexto, tema principal e biografia antes de disponibilizar o FAO.",
      BOOK_ITEM_REQUIRED: "Somente livros e livretos podem ser vinculados ao catálogo.",
      BOOK_LINKS_REQUIRED: "Vincule todos os livros e livretos ao catálogo.",
      EDITORIAL_DRIVE_FOLDER_REQUIRED: "Informe a pasta do Google Drive de cada item do tipo livro para iniciar a produção.",
      BOOK_IN_USE: "Este livro está vinculado a um box. Troque os vínculos antes de apagá-lo.",
      INVALID_DOCUMENT: "O documento não atende aos formatos ou ao limite de 20 MB.",
      DOCUMENT_NOT_FOUND: "Documento não encontrado.",
      INVALID_IMAGE: "Use PNG, JPEG ou WebP de até 10 MB.",
      PRODUCT_IMAGE_NOT_FOUND: "Imagem não encontrada.",
      PRODUCT_IMAGE_REQUIRED_FOR_PUBLICATION: "Retire a publicação antes de excluir a única imagem pública.",
      MOCKUP_ASSETS_TOTAL_LIMIT: "Os arquivos para mockup deste item devem somar no máximo 10 MB.",
      MOCKUP_ASSET_NOT_FOUND: "Arquivo para mockup não encontrado.",
      MOCKUP_ASSET_IN_USE: "Este asset já possui arquivos em itens e não pode ser removido.",
      INVALID_MOCKUP_ASSET_NAME: "Informe um nome para cada asset de mockup.",
      DUPLICATE_PRINT_SHOP: "Já existe uma gráfica com este nome.",
      PRINT_SHOP_NOT_FOUND: "Gráfica não encontrada.",
      PRINT_SHOP_IN_USE: "Esta gráfica possui itens associados e não pode ser apagada.",
      DUPLICATE_ITEM_TYPE: "Já existe um tipo de item com esse código ou nome.",
      ITEM_TYPE_IN_USE: "Este tipo está em uso e não pode ser apagado.",
      DUPLICATE_ITEM_TYPE_VARIANT: "Já existe uma variante com esse identificador.",
      ITEM_TYPE_VARIANT_IN_USE: "Esta variante está em uso e não pode ser apagada.",
      KIT_TEMPLATE_NOT_FOUND: "Modelo de kit não encontrado.",
      DUPLICATE_USER: "Já existe um usuário com este e-mail.",
      USER_NOT_FOUND: "Usuário não encontrado.",
      USER_IN_USE: "Este usuário possui registros editoriais associados e não pode ser excluído.",
      CANNOT_DELETE_OWN_USER: "Você não pode excluir a própria conta.",
      CANNOT_CHANGE_OWN_ROLE: "Você não pode alterar o próprio papel administrativo.",
      LAST_ADMIN_REQUIRED: "O workspace precisa manter ao menos um usuário Admin.",
      EDITOR_IN_CHIEF_CONFLICT: "Cada selo tem apenas um editor-chefe.",
      INVALID_IMPRINT: "Selecione selos válidos.",
      INVALID_IMPRINT_LAUNCH: "Informe mês e ano de lançamento válidos.",
      INVALID_IMPRINT_LOGO: "Arquivo de logotipo inválido. Use PNG ou SVG de até 5 MB.",
      DUPLICATE_SKU: "Já existe um produto com este SKU.",
      SKU_ADMIN_REQUIRED: "Somente Admin pode alterar o SKU.",
      INVALID_DRIVE_FOLDER_URL: "Informe um link válido de pasta do Google Drive.",
      ACTIVE_PRODUCTION_CONFIRMATION_REQUIRED: "Produto em produção. Alterar mesmo assim?",
      ACTIVE_PRODUCTION_EXISTS: "Este produto já possui uma produção aberta.",
      OPEN_PRODUCTION_EXISTS: "Este produto já possui uma produção aberta.",
      OPEN_PRODUCTION_REQUIRED: "A produção não está aberta.",
      PRODUCTION_MUST_BE_CANCELLED: "Cancele a produção antes de apagá-la.",
      STARTABLE_CATALOG_PRODUCT_REQUIRED: "Somente produtos em rascunho ou prontos podem iniciar uma produção.",
      CATALOG_STATUS_MANAGED_BY_PRODUCTION: "Os status Em produção e Pronto são atualizados automaticamente pela produção.",
      PIPELINE_NOT_APPLICABLE: "O fluxo de trabalho não cobre todos os tipos de item deste produto.",
      PUBLISHED_PIPELINE_REQUIRED: "Selecione uma versão publicada do fluxo de trabalho.",
      INVALID_QUANTITY: "Informe uma quantidade válida.",
      INVALID_PRINT_CHECKLIST_KEY: "Item do checklist da produção gráfica não reconhecido.",
      INVALID_QUOTE_URL: "Informe um link de orçamento válido, começando com http:// ou https://.",
      INVALID_PRINT_DELIVERIES: "Revise as entregas da gráfica: datas no formato DD/MM/AAAA e tiragens em números inteiros.",
      INVALID_REFERENCE_MONTH: "Informe uma competência válida.",
      INVALID_PRODUCTION_STATUS: "Selecione um estado válido para a produção.",
      INVALID_ITEM_QUANTITIES: "Informe quantidades válidas para todos os itens ativos.",
      INVALID_STAGE_ASSIGNMENTS: "Revise os responsáveis: cada etapa aceita um administrador ou operador.",
      INVALID_ASSIGNEE: "Somente administradores e operadores podem ser responsáveis por etapas.",
      INVALID_PRODUCTION_LEAD: "O responsável geral precisa ser um administrador ou operador.",
      INVALID_STAGE: "A etapa não pertence ao fluxo de trabalho desta produção.",
      ITEM_CANCELLED: "O item foi cancelado e não pode avançar.",
      ITEM_BLOCKED: "Desbloqueie o item antes de avançar.",
      ACTIVE_STAGE_REQUIRED: "O item não possui uma etapa ativa.",
      BLOCKED_ITEM_REQUIRED: "O item não está bloqueado.",
      PRODUCT_HAS_PRODUCTION_HISTORY: "Produtos com histórico de produção não podem ser excluídos.",
      PUBLIC_SLUG_LOCKED: "O slug público fica permanente depois da primeira publicação.",
      INVALID_PUBLIC_SLUG: "Use identificador do selo e título em minúsculas, separados por barra; são aceitos números e hífens.",
      DUPLICATE_PUBLIC_SLUG: "Este slug público já está em uso.",
      INVALID_PUBLIC_MEDIA: "Revise a seleção, a ordem e os textos alternativos das imagens públicas.",
      PUBLIC_MEDIA_REQUIRED: "Selecione e salve pelo menos uma imagem pública.",
      PUBLIC_PRESENTATION_FIELDS_REQUIRED: "Nome e selo são obrigatórios para publicar.",
    }
    throw new DomainError(domainCode, messages[domainCode] ?? error.message, details)
  }
  return data as T
}

function imprintLogoUrl(storagePath: string) {
  return supabase?.storage.from("imprint-logos").getPublicUrl(storagePath).data.publicUrl ?? ""
}

const signedUrls = createSignedUrlCache(async (bucket, paths, expiresIn) => {
  if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
  const { data, error } = await supabase.storage.from(bucket).createSignedUrls(paths, expiresIn)
  if (error) throw new DomainError("IMAGE_DOWNLOAD_FAILED", error.message)
  const urls = new Map(data.flatMap((entry) => !entry.error && entry.path && entry.signedUrl ? [[entry.path, entry.signedUrl] as const] : []))
  return paths.map((path) => urls.get(path))
})

async function signedUrl(bucket: string, path: string) {
  const url = (await signedUrls.get(bucket, [path])).get(path)
  if (!url) throw new DomainError("IMAGE_DOWNLOAD_FAILED", "Arquivo não encontrado no armazenamento.")
  return url
}

function withThumbnailPaths(paths: string[]) {
  return paths.flatMap((path) => [path, thumbnailStoragePath(path)])
}

export class SupabaseRepository implements EditorialRepository {
  searchCatalog(query: string, filters: CatalogFilters = {}, pagination: CatalogPagination = {}) {
    return rpc<CatalogPage<CatalogSearchResult>>("search_catalog", {
      p_query: query,
      p_filters: filters,
      p_cursor: pagination.cursor,
      p_limit: pagination.limit,
    })
  }

  async listCatalogProducts(filters: CatalogFilters = {}, pagination: CatalogPagination = {}): Promise<CatalogPage<CatalogProductSummary>> {
    type CatalogProductSummaryRow = CatalogProductSummary & { mockupThumbnailStoragePath?: string }
    const page = await rpc<CatalogPage<CatalogProductSummaryRow>>("list_catalog_products", {
      p_filters: filters,
      p_cursor: pagination.cursor,
      p_limit: pagination.limit,
    })
    if (!supabase) return page
    const paths = [...new Set(page.items.flatMap((item) => item.mockupThumbnailStoragePath ? [item.mockupThumbnailStoragePath] : []))]
    if (!paths.length) return page
    let urls = new Map<string, string>()
    try {
      const thumbnails = await signedUrls.get("catalog-product-images", paths.map(thumbnailStoragePath))
      // Images uploaded before thumbnails existed fall back to the original.
      const originals = await signedUrls.get("catalog-product-images", paths.filter((path) => !thumbnails.has(thumbnailStoragePath(path))))
      urls = new Map(paths.flatMap((path) => {
        const url = thumbnails.get(thumbnailStoragePath(path)) ?? originals.get(path)
        return url ? [[path, url] as const] : []
      }))
    } catch {
      // Thumbnails are optional; the list still renders without them.
    }
    return {
      ...page,
      items: page.items.map(({ mockupThumbnailStoragePath, ...item }) => ({
        ...item,
        mockupThumbnailUrl: mockupThumbnailStoragePath ? urls.get(mockupThumbnailStoragePath) : undefined,
      })),
    }
  }

  listCatalogPublicationYears() {
    return rpc<number[]>("list_catalog_publication_years")
  }

  listCatalogCollections() {
    return rpc<CatalogCollectionSummary[]>("get_catalog_collections")
  }

  createCatalogCollection(input: CatalogCollectionInput) {
    return rpc<string>("admin_create_catalog_collection", { p_payload: input })
  }

  async updateCatalogCollection(collectionId: string, input: CatalogCollectionInput, expectedVersion: number) {
    await rpc("admin_update_catalog_collection", { p_collection_id: collectionId, p_payload: input, p_expected_version: expectedVersion })
  }

  async setCatalogCollectionActive(collectionId: string, active: boolean, reason: string, expectedVersion: number) {
    await rpc("admin_set_catalog_collection_active", { p_collection_id: collectionId, p_active: active, p_reason: reason, p_expected_version: expectedVersion })
  }

  async deleteCatalogCollection(collectionId: string) {
    await rpc("admin_delete_catalog_collection", { p_collection_id: collectionId })
  }

  getCatalogProduct(productId: string) {
    return rpc<CatalogProduct>("get_catalog_product", { p_product_id: productId })
  }

  getCatalogItem(itemId: string) {
    return rpc<CatalogItem>("get_catalog_item", { p_item_id: itemId })
  }

  listCatalogItemActivity(itemId: string, limit = 20, offset = 0) {
    return rpc<CatalogItemActivity[]>("list_catalog_item_activity", offset ? { p_item_id: itemId, p_limit: limit, p_offset: offset } : { p_item_id: itemId, p_limit: limit })
  }

  getCatalogItemEditionSnapshot(snapshotId: string) {
    return rpc<CatalogItemEditionSnapshot>("get_catalog_item_edition_snapshot", { p_snapshot_id: snapshotId })
  }

  createCatalogProduct(input: CreateCatalogProductInput) {
    return rpc<string>("create_catalog_product", { p_payload: input })
  }

  saveCatalogProduct(productId: string, input: SaveCatalogProductInput, expectedVersion: number, confirmActiveProduction = false) {
    return rpc<CatalogProduct>("save_catalog_product", {
      p_product_id: productId,
      p_payload: input,
      p_expected_version: expectedVersion,
      p_confirm_active_production: confirmActiveProduction,
    })
  }

  saveCatalogItem(itemId: string, input: CatalogItemInput, expectedVersion: number, confirmActiveProduction = false) {
    return rpc<CatalogItem>("save_catalog_item", {
      p_item_id: itemId,
      p_payload: input,
      p_expected_version: expectedVersion,
      p_confirm_active_production: confirmActiveProduction,
    })
  }

  createCatalogItemEdition(itemId: string, expectedVersion: number, confirmActiveProduction = false) {
    return rpc<CatalogItem>("create_catalog_item_edition", {
      p_item_id: itemId,
      p_expected_version: expectedVersion,
      p_confirm_active_production: confirmActiveProduction,
    })
  }

  async deleteCatalogProduct(productId: string, expectedVersion: number): Promise<DeleteCatalogProductResult> {
    const client = supabase
    if (!client) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    const result = await rpc<{ productImagePaths: string[]; documentPaths: string[]; itemMockupAssetPaths?: string[] }>("delete_catalog_product", {
      p_product_id: productId,
      p_expected_version: expectedVersion,
    })
    const removeFiles = async (bucket: string, paths: string[]) => {
      if (!paths.length) return false
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const { error } = await client.storage.from(bucket).remove(paths)
        if (!error) return false
      }
      return true
    }
    const [productImagesFailed, documentsFailed, itemMockupAssetsFailed] = await Promise.all([
      removeFiles("catalog-product-images", withThumbnailPaths(result.productImagePaths)),
      removeFiles("catalog-item-documents", result.documentPaths),
      removeFiles("catalog-item-mockup-assets", result.itemMockupAssetPaths ?? []),
    ])
    return { storageCleanupFailed: productImagesFailed || documentsFailed || itemMockupAssetsFailed }
  }

  listProductionPrintItems(runIds: string[]) {
    if (!runIds.length) return Promise.resolve([])
    return rpc<ProductionPrintItem[]>("list_production_print_items", { p_run_ids: runIds })
  }

  async listProductionRuns(filters: ProductionFilters = {}) {
    const items: ProductionRunSummary[] = []
    let cursor: string | null = null
    const limit = 100

    // This RPC returns only items. Follow its month/id cursor so callers that
    // sort or count the complete list do not silently stop at the first batch.
    while (true) {
      const page: { items: ProductionRunSummary[] } = await rpc("list_production_runs", { p_filters: filters, p_cursor: cursor, p_limit: limit })
      items.push(...page.items)
      if (page.items.length < limit) break
      const last: ProductionRunSummary = page.items[page.items.length - 1]
      cursor = `${last.referenceMonth}-01|${last.id}`
    }

    return items
  }

  getProductionRun(runId: string) {
    return rpc<ProductionRun>("get_production_run", { p_run_id: runId })
  }

  startProduction(input: StartProductionInput) {
    return rpc<string>("start_production", { p_payload: input })
  }

  updateProduction(input: UpdateProductionInput) {
    return rpc<ProductionRun>("update_production", { p_run_id: input.runId, p_payload: input, p_expected_version: input.expectedVersion })
  }

  updateProductionQuantities(input: UpdateProductionQuantitiesInput) {
    return rpc<ProductionRun>("update_production_quantities", {
      p_run_id: input.runId,
      p_kit_quantity: input.kitQuantity ?? null,
      p_item_quantities: input.itemQuantities,
      p_expected_version: input.expectedVersion,
    })
  }

  stopProduction(runId: string, expectedVersion: number, reason: string) {
    return rpc<ProductionRun>("stop_production", { p_run_id: runId, p_expected_version: expectedVersion, p_reason: reason })
  }

  resumeProduction(runId: string, expectedVersion: number) {
    return rpc<ProductionRun>("resume_production", { p_run_id: runId, p_expected_version: expectedVersion })
  }

  async deleteProduction(runId: string, expectedVersion: number) {
    await rpc("delete_production", { p_run_id: runId, p_expected_version: expectedVersion })
  }

  cancelProduction(runId: string, expectedVersion: number, reason: string) {
    return rpc<ProductionRun>("cancel_production", { p_run_id: runId, p_expected_version: expectedVersion, p_reason: reason })
  }

  async followProduction(runId: string, follow: boolean) {
    await rpc("follow_production", { p_run_id: runId, p_follow: follow })
  }

  transitionProductionItem(input: TransitionItemInput) {
    return rpc<ProductionRun>("transition_production_item", {
      p_run_id: input.runId,
      p_production_item_id: input.itemId,
      p_target_stage_id: input.targetStageId,
      p_expected_version: input.expectedVersion,
      p_reason: input.reason ?? null,
    })
  }

  returnProductionItem(input: ReturnProductionItemInput) {
    return rpc<ProductionRun>("return_production_item", {
      p_run_id: input.runId,
      p_production_item_id: input.itemId,
      p_target_stage_id: input.targetStageId,
      p_expected_version: input.expectedVersion,
      p_reason: input.reason,
    })
  }

  listAssignableUsers() {
    return rpc<AssignableUser[]>("list_assignable_users")
  }

  setProductionStageAssignment(input: SetProductionStageAssignmentInput) {
    return rpc<ProductionRun>("set_production_stage_assignment", { p_run_id: input.runId, p_stage_id: input.stageId, p_user_id: input.userId ?? null })
  }

  setProductionLead(input: SetProductionLeadInput) {
    return rpc<ProductionRun>("set_production_lead", { p_run_id: input.runId, p_user_id: input.userId ?? null })
  }

  saveProductionSchedule(input: SaveProductionScheduleInput) {
    return rpc<ProductionRun>("save_production_schedule", {
      p_run_id: input.runId,
      p_payload: { targetEndDate: input.targetEndDate, stageDays: input.stageDays, reason: input.reason },
      p_expected_version: input.expectedVersion,
    })
  }

  listMyTasks() {
    return rpc<MyTask[]>("get_my_tasks")
  }

  skipProductionItem(input: SkipProductionItemInput) {
    return rpc<ProductionRun>("skip_production_item", {
      p_run_id: input.runId,
      p_production_item_id: input.itemId,
      p_target_stage_id: input.targetStageId,
      p_expected_version: input.expectedVersion,
      p_reason: input.reason,
    })
  }

  blockProductionItem(input: BlockItemInput) {
    return rpc<ProductionRun>("set_production_item_blocked", {
      p_run_id: input.runId,
      p_production_item_id: input.itemId,
      p_reason: input.reason,
      p_expected_version: input.expectedVersion,
    })
  }

  unblockProductionItem(input: Omit<BlockItemInput, "reason">) {
    return rpc<ProductionRun>("set_production_item_unblocked", {
      p_run_id: input.runId,
      p_production_item_id: input.itemId,
      p_expected_version: input.expectedVersion,
    })
  }

  toggleProductionChecklist(checklistId: string, expectedVersion: number) {
    return rpc<ProductionRun>("toggle_production_checklist", { p_checklist_id: checklistId, p_expected_version: expectedVersion })
  }

  toggleProductionGate(gateId: string, expectedVersion: number) {
    return rpc<ProductionRun>("toggle_production_gate", { p_gate_id: gateId, p_expected_version: expectedVersion })
  }

  assignProductionItemPrintShop(itemId: string, printShopId: string | undefined, expectedVersion: number) {
    return rpc<ProductionRun>("assign_production_item_print_shop", {
      p_production_item_id: itemId,
      p_print_shop_id: printShopId ?? null,
      p_expected_version: expectedVersion,
    })
  }

  setProductionItemPrintDeliveries(itemId: string, deliveries: ProductionPrintDelivery[], expectedVersion: number) {
    return rpc<ProductionRun>("set_production_item_print_deliveries", {
      p_production_item_id: itemId,
      p_deliveries: deliveries,
      p_expected_version: expectedVersion,
    })
  }

  setProductionItemPrintChecklist(itemId: string, key: PrintChecklistKey, done: boolean, expectedVersion: number) {
    return rpc<ProductionRun>("set_production_item_print_checklist", {
      p_production_item_id: itemId,
      p_key: key,
      p_done: done,
      p_expected_version: expectedVersion,
    })
  }

  setProductionItemQuoteUrl(itemId: string, quoteUrl: string | null, expectedVersion: number) {
    return rpc<ProductionRun>("set_production_item_quote_url", {
      p_production_item_id: itemId,
      p_quote_url: quoteUrl,
      p_expected_version: expectedVersion,
    })
  }

  getItemTypeCatalog() {
    return rpc<CatalogItemType[]>("get_item_type_catalog")
  }

  getKitPresets() {
    return rpc<KitPresetCatalogEntry[]>("get_kit_presets")
  }

  async getPipelineCatalog() {
    const catalog = await rpc<Array<Omit<PipelineCatalogEntry, "versions"> & { versions: LegacyPipelineVersion[] }>>("get_pipeline_catalog")
    return catalog.map((template) => ({ ...template, versions: normalizePipelineVersions(template.versions) }))
  }

  async uploadCatalogItemDocument(input: UploadCatalogItemDocumentInput) {
    if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    if (input.file.size <= 0 || input.file.size > 20_000_000) throw new DomainError("INVALID_DOCUMENT", "O arquivo deve ter até 20 MB.")
    const mimeType = normalizeDocumentMime(input.file)
    const extension = input.file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "")
    const storagePath = `${input.itemId}/${input.kind.toLowerCase()}/${crypto.randomUUID()}${extension ? `.${extension}` : ""}`
    const sha256 = await fileSha256(input.file)
    const { error } = await supabase.storage.from("catalog-item-documents").upload(storagePath, input.file, { contentType: mimeType, upsert: false })
    if (error) throw new DomainError("DOCUMENT_UPLOAD_FAILED", error.message)
    try {
      return await rpc<CatalogItem>("register_catalog_item_document", {
        p_item_id: input.itemId,
        p_kind: input.kind,
        p_storage_path: storagePath,
        p_original_filename: input.file.name,
        p_mime_type: mimeType,
        p_size_bytes: input.file.size,
        p_sha256: sha256,
        p_expected_version: input.expectedVersion,
        p_confirm_active_production: input.confirmActiveProduction ?? false,
      })
    } catch (cause) {
      await supabase.storage.from("catalog-item-documents").remove([storagePath])
      throw cause
    }
  }

  async getCatalogItemDocumentUrl(document: CatalogItemDocument) {
    if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    const { data, error } = await supabase.storage.from("catalog-item-documents").createSignedUrl(document.storagePath, 600)
    if (error) throw new DomainError("DOCUMENT_DOWNLOAD_FAILED", error.message)
    return data.signedUrl
  }

  async deleteCatalogItemDocument(input: DeleteCatalogItemDocumentInput): Promise<DeleteCatalogItemDocumentResult> {
    if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    const result = await rpc<{ item: CatalogItem; storagePath?: string; storageRetainedForHistory?: boolean }>("delete_catalog_item_document", {
      p_item_id: input.itemId,
      p_document_id: input.documentId,
      p_expected_version: input.expectedVersion,
      p_confirm_active_production: input.confirmActiveProduction ?? false,
    })
    if (!result.storagePath) {
      return { item: result.item, storageCleanupFailed: false, storageRetainedForHistory: result.storageRetainedForHistory }
    }
    let storageCleanupFailed = false
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const { error } = await supabase.storage.from("catalog-item-documents").remove([result.storagePath])
      if (!error) return { item: result.item, storageCleanupFailed: false, storageRetainedForHistory: false }
      storageCleanupFailed = true
    }
    return { item: result.item, storageCleanupFailed, storageRetainedForHistory: false }
  }

  async uploadCatalogItemMockupAsset(input: UploadCatalogItemMockupAssetInput) {
    if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    const replacedSize = input.replacedAssetSizeBytes ?? 0
    if (input.file.size <= 0 || !["image/png", "image/jpeg", "image/webp"].includes(input.file.type)) {
      throw new DomainError("INVALID_IMAGE", "Use PNG, JPEG ou WebP.")
    }
    if (input.currentTotalSizeBytes - replacedSize + input.file.size > 10_000_000) {
      throw new DomainError("MOCKUP_ASSETS_TOTAL_LIMIT", "Os arquivos para mockup deste item devem somar no máximo 10 MB.")
    }
    const extension = input.file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "")
    const storagePath = `${input.itemId}/${input.assetDefinitionId}/${crypto.randomUUID()}${extension ? `.${extension}` : ""}`
    const { error } = await supabase.storage.from("catalog-item-mockup-assets").upload(storagePath, input.file, { contentType: input.file.type, upsert: false })
    if (error) throw new DomainError("IMAGE_UPLOAD_FAILED", error.message)
    try {
      const result = await rpc<{ item: CatalogItem; previousStoragePath?: string }>("register_catalog_item_mockup_asset", {
        p_item_id: input.itemId,
        p_asset_definition_id: input.assetDefinitionId,
        p_storage_path: storagePath,
        p_original_filename: input.file.name,
        p_mime_type: input.file.type,
        p_size_bytes: input.file.size,
        p_expected_version: input.expectedVersion,
        p_confirm_active_production: input.confirmActiveProduction ?? false,
      })
      if (result.previousStoragePath) await supabase.storage.from("catalog-item-mockup-assets").remove([result.previousStoragePath])
      return result.item
    } catch (cause) {
      await supabase.storage.from("catalog-item-mockup-assets").remove([storagePath])
      throw cause
    }
  }

  async deleteCatalogItemMockupAsset(input: DeleteCatalogItemMockupAssetInput): Promise<DeleteCatalogItemMockupAssetResult> {
    if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    const result = await rpc<{ item: CatalogItem; storagePath: string }>("delete_catalog_item_mockup_asset", {
      p_item_id: input.itemId,
      p_asset_id: input.assetId,
      p_expected_version: input.expectedVersion,
      p_confirm_active_production: input.confirmActiveProduction ?? false,
    })
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const { error } = await supabase.storage.from("catalog-item-mockup-assets").remove([result.storagePath])
      if (!error) return { item: result.item, storageCleanupFailed: false }
    }
    return { item: result.item, storageCleanupFailed: true }
  }

  getCatalogItemMockupAssetUrl(asset: CatalogItemMockupAsset) {
    return signedUrl("catalog-item-mockup-assets", asset.storagePath)
  }

  async uploadCatalogProductImage(input: UploadCatalogProductImageInput) {
    if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    if (input.file.size <= 0 || input.file.size > 10_000_000 || !["image/png", "image/jpeg", "image/webp"].includes(input.file.type)) {
      throw new DomainError("INVALID_IMAGE", "Use PNG, JPEG ou WebP de até 10 MB.")
    }
    const extension = input.file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "")
    const storagePath = `${input.productId}/${input.kind.toLowerCase()}/${crypto.randomUUID()}${extension ? `.${extension}` : ""}`
    const { error } = await supabase.storage.from("catalog-product-images").upload(storagePath, input.file, { contentType: input.file.type, cacheControl: immutableCacheControl, upsert: false })
    if (error) throw new DomainError("IMAGE_UPLOAD_FAILED", error.message)
    try {
      // Best effort: without a thumbnail the app shows the original instead.
      const thumbnail = await createImageThumbnail(input.file)
      await supabase.storage.from("catalog-product-images").upload(thumbnailStoragePath(storagePath), thumbnail.blob, {
        contentType: thumbnail.blob.type,
        cacheControl: immutableCacheControl,
        metadata: { width: thumbnail.width, height: thumbnail.height },
        upsert: true,
      })
    } catch {
      // The upload itself succeeded; `catalog:thumbnails` can fill the gap later.
    }
    try {
      return await rpc<CatalogProduct>("register_catalog_product_image", {
        p_product_id: input.productId,
        p_kind: input.kind,
        p_storage_path: storagePath,
        p_original_filename: input.file.name,
        p_mime_type: input.file.type,
        p_size_bytes: input.file.size,
        p_expected_version: input.expectedVersion,
      })
    } catch (cause) {
      await supabase.storage.from("catalog-product-images").remove(withThumbnailPaths([storagePath]))
      throw cause
    }
  }

  getCatalogProductImageUrl(image: CatalogProductImage) {
    return signedUrl("catalog-product-images", image.storagePath)
  }

  async getCatalogProductImagePreview(image: CatalogProductImage): Promise<CatalogProductImagePreview> {
    if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    const path = thumbnailStoragePath(image.storagePath)
    const url = (await signedUrls.get("catalog-product-images", [path])).get(path)
    if (!url) return { url: await this.getCatalogProductImageUrl(image), isThumbnail: false }
    // The thumbnail carries the original dimensions as custom metadata.
    const { data } = await supabase.storage.from("catalog-product-images").info(path)
    const width = Number(data?.metadata?.width)
    const height = Number(data?.metadata?.height)
    return width > 0 && height > 0 ? { url, isThumbnail: true, width, height } : { url, isThumbnail: true }
  }

  async getMockupStudioKit(productId: string) {
    if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    const { data, error } = await supabase.functions.invoke<{ data?: unknown }>(`catalog-mockup-v1/v2/kits/${encodeURIComponent(productId)}`, { method: "GET" })
    if (error || !data?.data) {
      const context = (error as { context?: unknown } | null)?.context
      const payload = context instanceof Response ? await context.json().catch(() => null) as { error?: { code?: string; message?: string } } | null : null
      throw new DomainError(payload?.error?.code ?? "MOCKUP_KIT_UNAVAILABLE", payload?.error?.message ?? "Não foi possível carregar o kit para o Mockup Studio.")
    }
    return data.data
  }

  async stageCatalogProductImages(productId: string, files: File[], onProgress?: (uploaded: number) => void) {
    const client = supabase
    if (!client) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    if (files.some((file) => file.size <= 0 || file.size > 10_000_000 || !["image/png", "image/jpeg", "image/webp"].includes(file.type))) {
      throw new DomainError("INVALID_IMAGE", "Use PNG, JPEG ou WebP de até 10 MB.")
    }
    const staged: StagedCatalogProductImage[] = []
    try {
      for (const file of files) {
        const extension = file.type.split("/")[1].replace("jpeg", "jpg")
        const storagePath = `${productId}/mockup/${crypto.randomUUID()}.${extension}`
        const { error } = await client.storage.from("catalog-product-images").upload(storagePath, file, { contentType: file.type, cacheControl: immutableCacheControl, upsert: false })
        if (error) throw new DomainError("IMAGE_UPLOAD_FAILED", error.message)
        staged.push({ storagePath, originalFilename: file.name, mimeType: file.type, sizeBytes: file.size })
        try {
          // Best effort: without a thumbnail the app shows the original instead.
          const thumbnail = await createImageThumbnail(file)
          await client.storage.from("catalog-product-images").upload(thumbnailStoragePath(storagePath), thumbnail.blob, {
            contentType: thumbnail.blob.type,
            cacheControl: immutableCacheControl,
            metadata: { width: thumbnail.width, height: thumbnail.height },
            upsert: true,
          })
        } catch {
          // `catalog:thumbnails` can fill the gap later.
        }
        onProgress?.(staged.length)
      }
      return staged
    } catch (cause) {
      await this.discardStagedCatalogProductImages(staged)
      throw cause
    }
  }

  async replaceStudioKitImages(productId: string, images: StagedCatalogProductImage[], expectedVersion: number): Promise<ReplaceStudioKitImagesResult> {
    const client = supabase
    if (!client) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    const result = await rpc<{ product: CatalogProduct; removedStoragePaths: string[]; keptPublicCount: number }>("replace_catalog_product_studio_kit_images", {
      p_product_id: productId,
      p_images: images,
      p_expected_version: expectedVersion,
    })
    let storageCleanupFailed = false
    if (result.removedStoragePaths.length) {
      const { error } = await client.storage.from("catalog-product-images").remove(withThumbnailPaths(result.removedStoragePaths))
      storageCleanupFailed = Boolean(error)
    }
    return { product: result.product, removedCount: result.removedStoragePaths.length, keptPublicCount: result.keptPublicCount, storageCleanupFailed }
  }

  async discardStagedCatalogProductImages(images: StagedCatalogProductImage[]) {
    if (!supabase || !images.length) return
    await supabase.storage.from("catalog-product-images").remove(withThumbnailPaths(images.map((image) => image.storagePath)))
  }

  async deleteCatalogProductImage(input: DeleteCatalogProductImageInput): Promise<DeleteCatalogProductImageResult> {
    const client = supabase
    if (!client) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    const result = await rpc<{ product: CatalogProduct; storagePath: string }>("delete_catalog_product_image", {
      p_product_id: input.productId,
      p_image_id: input.imageId,
      p_expected_version: input.expectedVersion,
    })
    let storageCleanupFailed = true
    for (let attempt = 0; attempt < 2 && storageCleanupFailed; attempt += 1) {
      const { error } = await client.storage.from("catalog-product-images").remove(withThumbnailPaths([result.storagePath]))
      storageCleanupFailed = Boolean(error)
    }
    return { product: result.product, storageCleanupFailed }
  }

  // O site do catálogo assina as imagens do bucket privado no build: salvar a seleção só grava ordem, principal e textos.
  async saveCatalogPublicMedia(productId: string, media: CatalogPublicMediaSelection[], expectedVersion: number, altTexts: CatalogImageAltText[] = [], { onProgress }: SaveCatalogPublicMediaOptions = {}) {
    onProgress?.({ step: "SAVING" })
    return rpc<CatalogProduct>("save_catalog_public_media", { p_product_id: productId, p_media: media, p_expected_version: expectedVersion, p_image_alt_texts: altTexts })
  }

  publishCatalogProduct(productId: string, publicSlug: string, expectedVersion: number) {
    return rpc<CatalogProduct>("publish_catalog_product", { p_product_id: productId, p_public_slug: publicSlug, p_expected_version: expectedVersion })
  }

  async unpublishCatalogProduct(productId: string, expectedVersion: number) {
    const result = await rpc<{ product: CatalogProduct }>("unpublish_catalog_product", { p_product_id: productId, p_expected_version: expectedVersion })
    return result.product
  }

  getDashboard() {
    return rpc<DashboardData>("get_dashboard")
  }

  listNotifications() {
    return Promise.resolve([] as NotificationItem[])
  }

  async markNotificationRead(_notificationId: string) {}

  async getTemplateCatalog() {
    const catalog = await rpc<Omit<TemplateCatalog, "pipelineTemplates"> & {
      pipelineTemplates: Array<Omit<TemplateCatalog["pipelineTemplates"][number], "versions"> & { versions: LegacyPipelineVersion[] }>
    }>("get_template_catalog")
    return {
      ...catalog,
      pipelineTemplates: catalog.pipelineTemplates.map((template) => ({ ...template, versions: normalizePipelineVersions(template.versions) })),
    }
  }

  createItemType(input: ItemTypeInput) {
    return rpc<string>("admin_create_item_type", { p_payload: input })
  }

  async updateItemType(itemTypeId: string, name: string) {
    await rpc("admin_update_item_type", { p_item_type_id: itemTypeId, p_name: name })
  }

  saveItemType(input: ItemTypeInput, itemTypeId?: string, expectedVersion?: number) {
    return rpc<string>("admin_save_item_type", { p_payload: input, p_item_type_id: itemTypeId ?? null, p_expected_version: expectedVersion ?? null })
  }

  async deleteItemType(itemTypeId: string, expectedVersion: number) {
    await rpc("admin_delete_item_type", { p_item_type_id: itemTypeId, p_expected_version: expectedVersion })
  }

  saveItemTypeVariant(itemTypeId: string, input: ItemTypeVariantInput, variantId?: string, expectedVersion?: number) {
    return rpc<string>("admin_save_item_type_variant", { p_item_type_id: itemTypeId, p_payload: input, p_variant_id: variantId ?? null, p_expected_version: expectedVersion ?? null })
  }

  async deleteItemTypeVariant(variantId: string, expectedVersion: number) {
    await rpc("admin_delete_item_type_variant", { p_variant_id: variantId, p_expected_version: expectedVersion })
  }

  createKitTemplate(input: CreateKitTemplateInput) {
    return rpc<string>("admin_create_kit_template", { p_payload: input })
  }

  updateKitTemplate(templateId: string, input: CreateKitTemplateInput, expectedVersion?: number) {
    return rpc<string>("admin_update_kit_template", { p_template_id: templateId, p_payload: input, p_expected_version: expectedVersion ?? null })
  }

  async deleteKitTemplate(templateId: string) {
    await rpc("admin_delete_kit_template", { p_template_id: templateId })
  }

  createPipelineTemplate(input: CreatePipelineTemplateInput) {
    return rpc<string>("admin_create_pipeline_template", { p_payload: input })
  }

  async updatePipelineTemplateVersion(versionId: string, input: PipelineVersionContentInput, expectedVersion?: number) {
    await rpc("admin_update_pipeline_template_version", { p_version_id: versionId, p_payload: input, p_expected_version: expectedVersion ?? null })
  }

  createPipelineTemplateVersion(sourceVersionId: string) {
    return rpc<string>("admin_create_pipeline_template_version", { p_source_version_id: sourceVersionId })
  }

  async publishPipelineTemplateVersion(versionId: string) {
    await rpc("admin_publish_pipeline_template_version", { p_version_id: versionId })
  }

  async setPipelineTemplateActive(templateId: string, active: boolean) {
    await rpc("admin_set_pipeline_template_active", { p_template_id: templateId, p_active: active })
  }

  async setPipelineStageFunction(stageId: string, editorialFunction: EditorialFunction | null) {
    await rpc("admin_set_pipeline_stage_function", { p_stage_id: stageId, p_function: editorialFunction })
  }

  async deletePipelineTemplate(templateId: string) {
    await rpc("admin_delete_pipeline_template", { p_template_id: templateId })
  }

  async listPipelineStageEstimates(pipelineTemplateId: string) {
    const result = await rpc<{ revision: number | null; estimates: Array<Omit<PipelineStageEstimate, "days"> & { days: number | string }> }>("get_pipeline_stage_estimates", { p_pipeline_template_id: pipelineTemplateId })
    return { revision: result.revision ?? undefined, estimates: result.estimates.map((estimate) => ({ ...estimate, days: Number(estimate.days) })) }
  }

  async savePipelineStageEstimates(input: SavePipelineStageEstimatesInput) {
    await rpc("admin_save_pipeline_stage_estimates", { p_pipeline_template_id: input.pipelineTemplateId, p_stage_names: input.stageNames, p_estimates: input.estimates, p_expected_version: input.expectedVersion ?? null })
  }

  async listImprints() {
    const imprints = await rpc<Array<Omit<ImprintSummary, "logos"> & { logos?: Array<Omit<ImprintLogo, "url">> }>>("get_imprints")
    return imprints.map((imprint) => ({ ...imprint, logos: (imprint.logos ?? []).map((logo) => ({ ...logo, url: imprintLogoUrl(logo.storagePath) })) }))
  }

  createImprint(input: ImprintInput) {
    return rpc<string>("admin_create_imprint", { p_payload: input })
  }

  async updateImprint(imprintId: string, input: ImprintInput, expectedVersion?: number) {
    await rpc("admin_update_imprint", { p_imprint_id: imprintId, p_payload: input, p_expected_version: expectedVersion ?? null })
  }

  async setImprintActive(imprintId: string, active: boolean, reason: string) {
    await rpc("admin_set_imprint_active", { p_imprint_id: imprintId, p_active: active, p_reason: reason })
  }

  async deleteImprint(imprintId: string) {
    const logoPaths = (await this.listImprints()).find((imprint) => imprint.id === imprintId)?.logos.map((logo) => logo.storagePath) ?? []
    await rpc("admin_delete_imprint", { p_imprint_id: imprintId })
    if (logoPaths.length && supabase) await supabase.storage.from("imprint-logos").remove(logoPaths)
  }

  async uploadImprintLogo(input: UploadImprintLogoInput) {
    if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    const fileError = imprintLogoFileError(input.format, input.file)
    if (fileError) throw new DomainError("INVALID_IMPRINT_LOGO", fileError)
    const extension = input.format.toLowerCase()
    const storagePath = `${input.imprintId}/${input.variant.toLowerCase()}-${extension}-${crypto.randomUUID()}.${extension}`
    const { error } = await supabase.storage.from("imprint-logos").upload(storagePath, input.file, { contentType: input.file.type, upsert: false })
    if (error) throw new DomainError("IMAGE_UPLOAD_FAILED", error.message)
    try {
      const result = await rpc<{ previousStoragePath?: string | null }>("admin_register_imprint_logo", {
        p_imprint_id: input.imprintId,
        p_variant: input.variant,
        p_format: input.format,
        p_storage_path: storagePath,
        p_original_filename: input.file.name,
        p_mime_type: input.file.type,
        p_size_bytes: input.file.size,
      })
      if (result.previousStoragePath) await supabase.storage.from("imprint-logos").remove([result.previousStoragePath])
    } catch (cause) {
      await supabase.storage.from("imprint-logos").remove([storagePath])
      throw cause
    }
  }

  async deleteImprintLogo(input: ImprintLogoTarget) {
    if (!supabase) throw new DomainError("CONFIGURATION_ERROR", "Supabase não está configurado.")
    const result = await rpc<{ storagePath: string }>("admin_delete_imprint_logo", { p_imprint_id: input.imprintId, p_variant: input.variant, p_format: input.format })
    await supabase.storage.from("imprint-logos").remove([result.storagePath])
  }

  listUsers() {
    return rpc<WorkspaceUser[]>("admin_list_users")
  }

  async inviteUser(input: InviteUserInput) {
    const data = await invokeFunction<{ id: string }>("admin-invite-user", { ...input })
    if (!data?.id) throw new DomainError("USER_CREATE_FAILED", "O usuário foi criado sem um identificador válido.")
    return data.id
  }

  async setUserRole(userId: string, role: WorkspaceUser["role"]) {
    await rpc("admin_set_user_role", { p_user_id: userId, p_role: role })
  }

  async setUserTeam(input: SetUserTeamInput) {
    await rpc("admin_set_user_team", { p_user_id: input.userId, p_functions: input.functions, p_imprint_ids: input.imprintIds, p_expected_version: input.expectedVersion ?? null })
  }

  async updateUser(input: UpdateUserInput) {
    await rpc("admin_update_user", { p_user_id: input.userId, p_name: input.name, p_functions: input.functions, p_imprint_ids: input.imprintIds, p_expected_version: input.expectedVersion ?? null })
  }

  async deleteUser(userId: string) {
    await rpc("admin_delete_user", { p_user_id: userId })
  }

  listPrintShops() {
    return rpc<PrintShopSummary[]>("get_print_shops")
  }

  createPrintShop(input: PrintShopInput) {
    return rpc<string>("admin_create_print_shop", { p_payload: input })
  }

  async updatePrintShop(printShopId: string, input: PrintShopInput, expectedVersion?: number) {
    await rpc("admin_update_print_shop", { p_print_shop_id: printShopId, p_payload: input, p_expected_version: expectedVersion ?? null })
  }

  async deletePrintShop(printShopId: string) {
    await rpc("admin_delete_print_shop", { p_print_shop_id: printShopId })
  }

  getGoogleDriveConnection() {
    return rpc<GoogleDriveConnection>("get_google_drive_connection")
  }

  async startGoogleDriveConnection(returnUrl: string) {
    const { authorizationUrl } = await invokeFunction<{ authorizationUrl: string }>("google-drive-connection", { action: "start", returnUrl })
    return authorizationUrl
  }

  async disconnectGoogleDrive() {
    await invokeFunction("google-drive-connection", { action: "disconnect" })
  }

  syncProductionWithDrive(input: SyncProductionWithDriveInput) {
    return invokeFunction<DriveSyncResult>("google-drive-sync-production", { ...input })
  }

  getDriveItemStageFolders(input: { runId: string; itemId: string }) {
    return invokeFunction<DriveItemStageFolders>("google-drive-item-stage-folders", { ...input })
  }

  async uploadSupplementaryFile(input: UploadSupplementaryFileInput): Promise<DriveUploadedFile> {
    const { mimeType } = SUPPLEMENTARY_FILE_TYPES[input.kind]
    const { folderName, folderUrl, uploadUrl, replaced } = await invokeFunction<{ uploadUrl: string; replaced: boolean; folderId: string; folderName: string; folderUrl: string }>("google-drive-catalog-record-upload-session", { itemId: input.itemId, kind: input.kind, fileName: input.file.name, size: input.file.size })
    const uploaded = await putFileToGoogleDrive(uploadUrl, input.file, mimeType, input.onProgress)
    return { id: uploaded.id, name: uploaded.name, webViewLink: uploaded.webViewLink, replaced, folderName, folderUrl }
  }

  trashSupplementaryFile(itemId: string, kind: SupplementaryFileKind) {
    return invokeFunction<{ trashed: boolean }>("google-drive-trash-catalog-record-file", { itemId, kind })
  }

  extractCreditsFromDrive(fileUrl: string) {
    return invokeFunction<CreditsExtraction>("google-drive-extract-catalog-record", { fileUrl, kind: "CREDITS" })
  }

  extractFaoFromDrive(fileUrl: string) {
    return invokeFunction<FaoExtraction>("google-drive-extract-catalog-record", { fileUrl, kind: "FAO" })
  }

  extractCatalogRecordFromDrive(fileUrl: string) {
    return invokeFunction<CatalogRecordExtraction>("google-drive-extract-catalog-record", { fileUrl })
  }

  createProductDriveFolders(input: CreateProductDriveFoldersInput) {
    return invokeFunction<ProductDriveFoldersResult>("google-drive-create-product-folders", { ...input })
  }

  async uploadFileToDriveStage(input: UploadToDriveStageInput): Promise<DriveUploadedFile> {
    const mimeType = input.file.type || "application/octet-stream"
    const { folderName, folderUrl, uploadUrl } = await invokeFunction<{ uploadUrl: string; folderId: string; folderName: string; folderUrl: string }>("google-drive-upload-session", { runId: input.runId, itemId: input.itemId, stageId: input.stageId, fileName: input.file.name, mimeType, size: input.file.size })
    const uploaded = await putFileToGoogleDrive(uploadUrl, input.file, mimeType, input.onProgress)
    return { id: uploaded.id, name: uploaded.name, webViewLink: uploaded.webViewLink, folderName, folderUrl }
  }
}

function normalizeDocumentMime(file: File) {
  if (file.type && file.type !== "application/octet-stream") {
    if (file.type === "image/jpg") return "image/jpeg"
    if (file.type === "text/x-markdown") return "text/markdown"
    return file.type
  }
  const extension = file.name.split(".").pop()?.toLowerCase()
  const byExtension: Record<string, string> = {
    pdf: "application/pdf",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    md: "text/markdown",
    txt: "text/plain",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
  }
  return byExtension[extension ?? ""] ?? "application/octet-stream"
}

async function fileSha256(file: File) {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer())
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("")
}
